import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Save,
  RefreshCw,
  Search,
  Trash2,
  ChevronDown,
  ChevronRight,
  Brain,
  Bell,
  Settings2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SentimentLayout } from "./layout";
import {
  fetchSentimentConfig,
  updateSentimentConfig,
  deleteSentimentConfig,
  discoverSentimentPipelines,
  fetchSentimentDefaultPrompt,
} from "@/lib/api";
import type { SentimentConfig } from "@/lib/sentiment-types";

// ---------------------------------------------------------------------------
// Collapsible section wrapper
// ---------------------------------------------------------------------------
function Section({
  icon: Icon,
  title,
  defaultOpen = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        <Icon className="h-3.5 w-3.5" />
        {title}
      </button>
      {open && <div className="pl-7 pb-3 space-y-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact inline field
// ---------------------------------------------------------------------------
function Field({
  label,
  children,
  hint,
  fullWidth,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? "space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline config card
// ---------------------------------------------------------------------------
function PipelineCard({
  config,
  onSave,
  onDelete,
  saving,
  deleting,
  defaultPrompt,
}: {
  config: SentimentConfig;
  onSave: (pipelineId: string, updates: Partial<SentimentConfig>) => void;
  onDelete: (pipelineId: string) => void;
  saving: boolean;
  deleting: boolean;
  defaultPrompt: string;
}) {
  const [isEnabled, setIsEnabled] = useState(config.isEnabled);
  const [modelId, setModelId] = useState(config.modelId);
  const [threshold, setThreshold] = useState(config.alertSeverityThreshold);
  const [webhookUrl, setWebhookUrl] = useState(config.teamsWebhookUrl || "");
  const [recipients, setRecipients] = useState(config.alertRecipients.join(", "));
  const [concurrency, setConcurrency] = useState(config.maxConcurrentLlmCalls);
  const [prompt, setPrompt] = useState(config.systemPrompt || "");
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    onSave(config.pipelineId, {
      isEnabled,
      modelId,
      alertSeverityThreshold: threshold,
      teamsWebhookUrl: webhookUrl || null,
      alertRecipients: recipients
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean),
      maxConcurrentLlmCalls: concurrency,
      systemPrompt: prompt || null,
    } as any);
    setDirty(false);
  };

  const usingDefaultPrompt = !prompt;

  return (
    <Card className="overflow-hidden">
      {/* Header row: name, ID, enabled toggle, delete */}
      <CardHeader className="flex flex-row items-center justify-between py-3 px-4 bg-muted/30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm font-semibold truncate">
                {config.pipelineName || config.pipelineId}
              </CardTitle>
              <Badge variant={isEnabled ? "success" : "secondary"} className="text-[10px] px-1.5 py-0">
                {isEnabled ? "Active" : "Disabled"}
              </Badge>
              {dirty && (
                <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                  Unsaved
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
              {config.pipelineId}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <Label htmlFor={`enabled-${config.pipelineId}`} className="text-xs text-muted-foreground">
              Enabled
            </Label>
            <Switch
              id={`enabled-${config.pipelineId}`}
              checked={isEnabled}
              onCheckedChange={(v) => {
                setIsEnabled(v);
                markDirty();
              }}
            />
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete pipeline configuration?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove the configuration for{" "}
                  <strong>{config.pipelineName || config.pipelineId}</strong>.
                  Existing analysis results will not be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(config.pipelineId)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>

      <CardContent className="px-4 py-3 space-y-1">
        {/* --- LLM Settings --- */}
        <Section icon={Brain} title="LLM & Analysis" defaultOpen>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Model" hint="OpenRouter model ID">
              <Input
                className="h-8 text-xs"
                value={modelId}
                onChange={(e) => {
                  setModelId(e.target.value);
                  markDirty();
                }}
              />
            </Field>
            <Field label="Max Concurrent Calls" hint="Parallel LLM requests">
              <Input
                className="h-8 text-xs"
                type="number"
                min={1}
                max={20}
                value={concurrency}
                onChange={(e) => {
                  setConcurrency(parseInt(e.target.value) || 5);
                  markDirty();
                }}
              />
            </Field>
          </div>
          <Field
            label={
              usingDefaultPrompt
                ? "System Prompt (using default)"
                : "System Prompt (custom)"
            }
            fullWidth
            hint="Leave empty to use the built-in default prompt. Customise to change how emails are classified."
          >
            <Textarea
              className="text-xs font-mono min-h-[120px] resize-y"
              placeholder={defaultPrompt}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                markDirty();
              }}
            />
            {prompt && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-[11px] px-2 text-muted-foreground"
                onClick={() => {
                  setPrompt("");
                  markDirty();
                }}
              >
                Reset to default
              </Button>
            )}
          </Field>
        </Section>

        <Separator />

        {/* --- Alert Settings --- */}
        <Section icon={Bell} title="Alerting">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Severity Threshold" hint="Alert on complaints >= this level (1-5)">
              <Input
                className="h-8 text-xs"
                type="number"
                min={1}
                max={5}
                value={threshold}
                onChange={(e) => {
                  setThreshold(parseInt(e.target.value) || 4);
                  markDirty();
                }}
              />
            </Field>
          </div>
          <Field label="Teams Webhook URL" fullWidth>
            <Input
              className="h-8 text-xs"
              value={webhookUrl}
              onChange={(e) => {
                setWebhookUrl(e.target.value);
                markDirty();
              }}
              placeholder="https://..."
            />
          </Field>
          <Field label="Alert Recipients" fullWidth hint="Comma-separated email addresses">
            <Input
              className="h-8 text-xs"
              value={recipients}
              onChange={(e) => {
                setRecipients(e.target.value);
                markDirty();
              }}
              placeholder="user@peterlang.com, user2@peterlang.com"
            />
          </Field>
        </Section>

        {/* Save bar */}
        <div className="flex justify-end pt-2">
          <Button size="sm" className="h-8 text-xs" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save Changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main config page
// ---------------------------------------------------------------------------
export default function SentimentConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["sentimentConfig"],
    queryFn: fetchSentimentConfig,
  });

  const promptQuery = useQuery({
    queryKey: ["sentimentDefaultPrompt"],
    queryFn: fetchSentimentDefaultPrompt,
    staleTime: Infinity,
  });

  const saveMutation = useMutation({
    mutationFn: ({
      pipelineId,
      updates,
    }: {
      pipelineId: string;
      updates: Partial<SentimentConfig>;
    }) => updateSentimentConfig(pipelineId, updates),
    onSuccess: () => {
      toast({ title: "Configuration saved" });
      queryClient.invalidateQueries({ queryKey: ["sentimentConfig"] });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (pipelineId: string) => deleteSentimentConfig(pipelineId),
    onSuccess: () => {
      toast({ title: "Pipeline removed" });
      queryClient.invalidateQueries({ queryKey: ["sentimentConfig"] });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    },
  });

  const discoverMutation = useMutation({
    mutationFn: discoverSentimentPipelines,
    onSuccess: (data) => {
      toast({
        title: "Pipelines discovered",
        description: `Found ${data.discovered} pipeline(s) from HubSpot`,
      });
      queryClient.invalidateQueries({ queryKey: ["sentimentConfig"] });
    },
    onError: (err: any) => {
      toast({ title: "Discovery failed", description: err.message, variant: "destructive" });
    },
  });

  const configs = query.data?.configs || [];
  const defaultPrompt = promptQuery.data?.prompt || "";

  return (
    <SentimentLayout>
      <div className="p-6 space-y-5 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Configuration
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pipeline settings, LLM model, prompt, and alerting
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => discoverMutation.mutate()}
              disabled={discoverMutation.isPending}
            >
              {discoverMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <Search className="h-3.5 w-3.5 mr-1.5" />
              )}
              Scan HubSpot
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => query.refetch()}>
              <RefreshCw className={`h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Content */}
        {query.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : query.isError ? (
          <Card className="border-destructive/30">
            <CardContent className="py-8 text-center">
              <p className="text-sm font-medium text-destructive mb-1">Failed to load configuration</p>
              <p className="text-xs text-muted-foreground mb-3">
                Make sure the API is running on port 7072.
              </p>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : configs.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Search className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">No pipelines configured</p>
              <p className="text-xs text-muted-foreground mb-4">
                Scan HubSpot to discover ticket pipelines and configure sentiment analysis.
              </p>
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => discoverMutation.mutate()}
                disabled={discoverMutation.isPending}
              >
                {discoverMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Search className="h-3.5 w-3.5 mr-1.5" />
                )}
                Scan HubSpot Pipelines
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {configs.map((config) => (
              <PipelineCard
                key={config.pipelineId}
                config={config}
                defaultPrompt={defaultPrompt}
                onSave={(pipelineId, updates) => saveMutation.mutate({ pipelineId, updates })}
                onDelete={(pipelineId) => deleteMutation.mutate(pipelineId)}
                saving={saveMutation.isPending}
                deleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </SentimentLayout>
  );
}
